import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {QueryStatus, useInfiniteQuery, useMutation} from '@tanstack/react-query';
import {FixedSizeList} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {Button, message, Modal, Radio} from 'antd';
import {CheckCircleOutlined} from '@ant-design/icons';
import {Link, useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import {SearchLinkWrapper} from './CandidatePool';
import {CandidatePoolType, ICandidate, IJobDemand, IJobDetails, ILocation, IPosition} from "../../services/types.ts";
import {alertOptions} from "../../util/utils.ts";
import {AuthContext} from "../../context/AuthContext.tsx";
import {addJobDemand, getCandidatePool, updateSearchById} from "../../services/api.ts";
import {useCandidateCount} from "./hooks/useCandidatePool.ts";
import {CandidateSearchFilters} from "./new/CandidateSearchFilters.tsx";
import {CandidateCountDisplay} from "./new/CandidateCountDisplay.tsx";
import {VirtualizedCandidateList} from "./new/VirtualizedCandidateList.tsx";
import {ProfessionFilterModal} from "./components/ProfessionFilterModal.tsx";
import {LocationFilterModal} from "./components/LocationFilterModal.tsx";
import {useQueryState} from 'nuqs';


// --- Constants ---
const LIMIT = 20;

// --- Styled Components specific to this container (if any) ---
const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 73px);
    box-sizing: border-box;
`;

export const CandidatePoolNew: React.FC = () => {
    // --- State ---
    const [selectedPosition, setSelectedPosition] = useQueryState<IPosition | null>(
        'position',
        {
            parse: (value: string) => value ? JSON.parse(value) as IPosition : null,
            serialize: (value: IPosition | null) => value ? JSON.stringify(value) : '',
            defaultValue: null
        }
    );
    const [selectedLocation, setSelectedLocation] = useQueryState<ILocation | null>(
        'location',
        {
            parse: (value: string) => value ? JSON.parse(value) as ILocation : null,
            serialize: (value: ILocation | null) => value ? JSON.stringify(value) : '',
            defaultValue: null
        }
    );
    const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [selectedAlertOption, setSelectedAlertOption] = useState(alertOptions[0]);
    const [savedSearchItem, setSavedSearchItem] = useState<IJobDemand | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);

    // --- Refs ---
    const listRef = useRef<FixedSizeList>(null);
    const infiniteLoaderRef = useRef<InfiniteLoader>(null);

    // --- Hooks ---
    const navigate = useNavigate();
    const {user, isNewUserBannerVisible, setBannerVisible} = useContext(AuthContext);
    const isPrivateUser = user?._client?.isPrivate;
    // --- Effects ---
    useEffect(() => {
        const storedTarget = localStorage.getItem('redirectPath');
        if (storedTarget && storedTarget !== "/") {
            localStorage.removeItem("redirectPath");
            navigate(storedTarget, {replace: true});
        }
    }, [navigate]);

    // --- Data Fetching ---
    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        status,
    } = useInfiniteQuery<CandidatePoolType, Error, CandidatePoolType, [string, string | number | undefined, string | number | undefined], number>({
        queryKey: ['candidatePool', selectedPosition?.id ?? "all", selectedLocation?.id ?? "all"],
        queryFn: ({pageParam = 0}) =>
            getCandidatePool({
                limit: LIMIT,
                offset: pageParam,
                position: selectedPosition ?? undefined,
                location: selectedLocation ?? undefined,
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
            interface CandidateResponse {
                candidates?: ICandidate[];
                pages?: ICandidate[];
                totalCount?: number;
                total_count?: number;
                count?: number;
            }

            // Ensure lastPage is treated correctly, might be ICandidate[] or a different structure
            const candidatesOnLastPage = Array.isArray(lastPage) ? lastPage :
                // Handle different possible shapes of the response
                'candidates' in (lastPage as CandidateResponse) ? ((lastPage as CandidateResponse).candidates || []) :
                    'pages' in (lastPage as CandidateResponse) ? ((lastPage as CandidateResponse).pages || []) : [];

            const lastPageLength = candidatesOnLastPage.filter((c: ICandidate) => c?.id !== -1).length;

            // Calculate current total from all pages
            const currentTotal = allPages.flatMap(page => {
                const typedPage = page as (ICandidate[] | CandidateResponse);
                if (Array.isArray(typedPage)) return typedPage;
                if ('candidates' in typedPage) return typedPage.candidates || [];
                if ('pages' in typedPage) return typedPage.pages || [];
                return [];
            }).length;

            // Try to get totalCount if available, different API versions might return it in different ways
            const typedLastPage = lastPage as CandidateResponse;
            const totalCount = typedLastPage.totalCount || typedLastPage.total_count || typedLastPage.count;

            // Use total count if available
            if (totalCount !== undefined && currentTotal >= totalCount) {
                return undefined; // No more pages if we've reached the total count
            }

            // Fallback to page length check
            return lastPageLength === LIMIT ? currentTotal : undefined;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: selectedPosition === null || typeof selectedPosition?.id === 'number', // Only run when position is resolved (null or valid object)
    });

    const {
        data: numberOfCandidate,
        isLoading: isLoadingCount
    } = useCandidateCount(selectedPosition || undefined, selectedLocation || undefined);

    // --- Mutations ---
    const {mutate: saveSearchMutation, isPending: isSavingSearch} = useMutation({
        mutationFn: (jobDemand: Omit<IJobDemand, 'id' | 'client_id'>) => addJobDemand(jobDemand as IJobDemand),
        onSuccess: (response) => {
            setSavedSearchItem(response); // Store response temporarily for alert modal
            setIsAlertModalOpen(true);
            message.success('Suche gespeichert. Richten Sie nun Benachrichtigungen ein.');
        },
        onError: (error: Error | { message?: string }) => {
            console.error('Error saving filter:', error);
            message.error(`Speichern fehlgeschlagen: ${error?.message || 'Bitte versuchen Sie es erneut.'}`);
        },
    });

    const {mutate: updateSearchMutation, isPending: isUpdatingSearch} = useMutation({
        mutationFn: (updatedData: Partial<IJobDetails>) => updateSearchById(savedSearchItem?.id ?? 0, updatedData),
        onSuccess: () => {
            message.success('Benachrichtigungseinstellungen gespeichert');
            setIsAlertModalOpen(false);
            setSavedSearchItem(null); // Clear temp item after saving
        },
        onError: (error: Error | { message?: string }) => {
            message.error(`Update fehlgeschlagen: ${error?.message ?? 'Bitte versuchen Sie es erneut.'}`);
        },
    });

    // --- Memos and Callbacks ---
    const candidates = useMemo(() => {
        if (!data?.pages) return [];

        return data.pages.flatMap(page => {
            if (Array.isArray(page)) return page;
            if ('candidates' in page && Array.isArray(page.candidates)) return page.candidates;
            if ('pages' in page && Array.isArray(page.pages)) return page.pages;
            return [page]; // Treat as single candidate if no array found
        }).filter((candidate): candidate is ICandidate => !!candidate?.registered_at);
    }, [data]);


    // Reset infinite loader cache and scroll to top when filters change
    React.useEffect(() => {
        infiniteLoaderRef.current?.resetloadMoreItemsCache();
        listRef.current?.scrollToItem(0);
    }, [selectedPosition, selectedLocation]);

    const loadMoreItems = useCallback(() => {
        // Only fetch next page if not already fetching and there is a next page
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    // isItemLoaded tells InfiniteLoader if an item at a specific index is loaded
    const isItemLoaded = useCallback((index: number) => !hasNextPage || index < candidates.length, [hasNextPage, candidates.length]);

    // Calculate total item count for the list (candidates + 1 for loading indicator if hasNextPage)
    const itemCount = hasNextPage ? candidates.length + 1 : candidates.length;

    // --- Event Handlers ---
    const handleSavePosition = useCallback((position: IPosition | null) => {
        setSelectedPosition(position);
        setIsPositionModalOpen(false); // Close modal on save
    }, [setSelectedPosition]);

    const handleSaveLocation = useCallback((location: ILocation | null) => {
        setSelectedLocation(location);
        setIsLocationModalOpen(false); // Close modal on save
    }, [setSelectedLocation]);

    const handleClearFilters = useCallback(() => {
        setSelectedPosition(null);
        setSelectedLocation(null);
        // Optionally reset other related state if needed
    }, [setSelectedLocation, setSelectedPosition]);

    const handleSaveFilterClick = useCallback(() => {
        if (!selectedPosition?.id) {
            message.warning('Bitte wählen Sie zuerst aus, was Sie suchen (Beruf/Tätigkeit).');
            return;
        }
        if (!user?.id) {
            message.error('Fehler: Benutzer nicht gefunden. Bitte neu anmelden.');
            return;
        }

        const jobDemandPayload = {
            type: "candidate_search",
            search_what_id: selectedPosition.id,
            search_location_id: selectedLocation?.id || null,
        };
        saveSearchMutation(jobDemandPayload as IJobDemand);

    }, [user?.id, selectedPosition, selectedLocation, saveSearchMutation]);


    const handleSaveAlertSettings = useCallback(() => {
        if (!savedSearchItem?.id) {
            message.error("Fehler: Gespeicherte Suche nicht gefunden zum Aktualisieren.");
            return;
        }
        if (!selectedAlertOption) {
            message.warning("Bitte wählen Sie eine Benachrichtigungsoption aus.");
            return;
        }
        updateSearchMutation({
            alert_label: selectedAlertOption.label,
            alert_config_id: selectedAlertOption.id,
        });
    }, [savedSearchItem?.id, selectedAlertOption, updateSearchMutation]);

    // --- Render Logic ---
    return (
        <Container>
            <CandidateSearchFilters
                selectedPosition={selectedPosition}
                selectedLocation={selectedLocation}
                onPositionClick={() => setIsPositionModalOpen(true)}
                onLocationClick={() => setIsLocationModalOpen(true)}
                onClearFilters={handleClearFilters}
                onSaveSearchClick={handleSaveFilterClick}
                isSaveSearchDisabled={!selectedPosition?.id}
                isSavingSearch={isSavingSearch}
            />

            <CandidateCountDisplay
                count={numberOfCandidate?.id}
                isLoading={isLoadingCount}
            />

            <VirtualizedCandidateList
                candidates={candidates}
                status={status as QueryStatus}
                error={error}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                itemCount={itemCount}
                loadMoreItems={loadMoreItems}
                isItemLoaded={isItemLoaded}
                listRef={listRef}
                infiniteLoaderRef={infiniteLoaderRef}
            />

            <ProfessionFilterModal
                isOpen={isPositionModalOpen}
                onClose={() => setIsPositionModalOpen(false)}
                onSave={handleSavePosition}
                initialSelectedPosition={selectedPosition}
            />
            <LocationFilterModal
                isOpen={isLocationModalOpen}
                onClose={() => setIsLocationModalOpen(false)}
                onSave={handleSaveLocation}
                initialSelectedLocation={selectedLocation}
            />

            <Modal
                title="Benachrichtigung per Email einrichten"
                open={isAlertModalOpen}
                onCancel={() => setIsAlertModalOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsAlertModalOpen(false)} disabled={isUpdatingSearch}>
                        Abbrechen
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleSaveAlertSettings} loading={isUpdatingSearch}
                            disabled={isUpdatingSearch}>
                        Speichern & Schließen
                    </Button>,
                ]}
            >
                {savedSearchItem && (
                    <>
                        <SearchLinkWrapper>
                            <CheckCircleOutlined style={{color: 'green', fontSize: '1.2em', marginRight: '8px'}}/>
                            <div>
                                <p style={{margin: 0}}>Ihre Suche wurde gespeichert. Eine Übersicht finden Sie
                                    unter:</p>
                                <Link to="/searches">{"Mein Konto > Meine Suchen"}</Link>
                            </div>
                        </SearchLinkWrapper>
                        <p style={{marginTop: '16px'}}>Wie häufig möchten Sie über passende neue Kandidaten informiert
                            werden?</p>
                        <Radio.Group
                            onChange={(e) => setSelectedAlertOption(alertOptions.find(option => option.id === e.target.value) || alertOptions[0])}
                            value={selectedAlertOption?.id} // Use optional chaining
                            style={{display: 'flex', flexDirection: 'column', gap: '10px'}}
                        >
                            {alertOptions.map(option => (
                                <Radio key={option.id} value={option.id}>{option.label}</Radio>
                            ))}
                        </Radio.Group>
                    </>
                )}
            </Modal>

            {/* Welcome Banner Modals */}
            <Modal
                title={isPrivateUser ? "Willkommen bei silvertalent" : "So einfach finden Sie erfahrene Fachkräfte in Deutschlands größtem Ü60 Netzwerk"}
                open={isNewUserBannerVisible}
                onCancel={() => setBannerVisible(false)}
                footer={null}
            >
                <div style={{display: "flex", flexDirection: "column", gap: "20px"}}>
                    <ul>
                        <li><b>Kandidatenpool kostenlos durchsuchen</b> <br/>
                            Stöbern Sie kostenlos in Deutschland's größtem Ü60 Kandidatenpool - wir haben erfahrene Fachkräfte aus nahezu jeder Branche.
                        </li>
                        <li><b>Inserieren ab 0 €</b> <br/>
                            Schalten Sie eine Stellenanzeige um Kandidaten zu erreichen und erhöhen Sie Ihre Chance auf Bewerbungen indem Sie die Anzeige hervorheben.
                        </li>
                        <li><b>Kandidaten direkt kontaktieren</b> <br/>
                            Beim Schalten der Anzeige können Sie die Funktion "Direktansprache" aktivieren um auch gezielt einzelne Profile kontaktieren zu können.
                        </li>
                    </ul>
                    <Button type="primary" onClick={() => {
                        setBannerVisible(false);
                        navigate(user?._client?.profile_name ? '/new/job' : '/new/profile');
                    }}>
                        Jetzt inserieren ab 0€
                    </Button>
                </div>
            </Modal>

        </Container>
    );
};
